import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Send, CheckCircle, Building, Mail, Phone, User, MessageSquare } from 'lucide-react';

const RequestQuote = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  });

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `Company: ${formData.company}\\nInterest: ${formData.interest}\\n\\n${formData.message}`,
        }),
      });

      if (response.ok) {
        toast.success('Quote request submitted successfully! We will contact you soon.');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          interest: '',
          message: '',
        });
      } else {
        toast.error('Failed to submit request. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    'Oil & Gas Products',
    'Industrial Machinery',
    'Electronics & Diodes',
    'Agricultural Commodities',
    'Global Trading Services',
    'Logistics & Supply Chain',
    'Marketing Services',
    'Other',
  ];

  return (
    <>
      <Helmet>
        <title>Request a Quote | Eaglesway Group - Get Started</title>
        <meta 
          name="description" 
          content="Request a quote from Eaglesway Group for trading, logistics, and business solutions. Quick response guaranteed." 
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>
          </div>

          <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="mb-6 inline-flex items-center px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Send className="w-4 h-4 text-[#D4AF37] mr-2" />
              <span className="text-[#D4AF37] text-sm font-medium">Quick Response Guaranteed</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Request a <span className="text-[#D4AF37]">Quote</span>
            </h1>
            
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Tell us about your requirements and our team will provide a customized solution within 24 hours
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-12 pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Info Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Why Choose Us?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start text-gray-300 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                      Fast response within 24 hours
                    </li>
                    <li className="flex items-start text-gray-300 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                      Competitive pricing
                    </li>
                    <li className="flex items-start text-gray-300 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                      Global delivery network
                    </li>
                    <li className="flex items-start text-gray-300 text-sm">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                      Trusted by international clients
                    </li>
                  </ul>
                </Card>

                <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">Contact Info</h3>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-center text-gray-300">
                      <Phone className="w-5 h-5 text-[#D4AF37] mr-3" />
                      +971 52 571 2721
                    </div>
                    <div className="flex items-center text-gray-300">
                      <Mail className="w-5 h-5 text-[#D4AF37] mr-3" />
                      contact@eagles-way.com
                    </div>
                  </div>
                </Card>
              </div>

              {/* Quote Form */}
              <div className="lg:col-span-2">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        <User className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        placeholder="John Smith"
                      />
                    </div>

                    {/* Company Name */}
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                        <Building className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                        Company Name *
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                        placeholder="Your Company Ltd"
                      />
                    </div>

                    {/* Email and Phone */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                          <Mail className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                          Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          placeholder="john@company.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                          <Phone className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                          Phone Number *
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                    </div>

                    {/* Interest */}
                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium text-gray-300 mb-2">
                        Product / Service Interest *
                      </label>
                      <select
                        id="interest"
                        name="interest"
                        required
                        value={formData.interest}
                        onChange={handleChange}
                        className="w-full bg-black border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#D4AF37] focus:ring-[#D4AF37] focus:outline-none"
                      >
                        <option value="">Select an option</option>
                        {interestOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        <MessageSquare className="w-4 h-4 inline mr-2 text-[#D4AF37]" />
                        Message / Requirements *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-black border-gray-700 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                        placeholder="Tell us about your requirements, quantities, specifications, delivery location, etc."
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-8 py-6 text-lg transition-all duration-300 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      By submitting this form, you agree to be contacted by our team regarding your inquiry.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RequestQuote;
